import {MenuItem, Select, SelectChangeEvent, Card, CardContent, Avatar, Chip, Badge, IconButton} from "@mui/material";
import {useContext, useEffect, useState} from "react";
import {LoginContext} from "../../contexts/LoginContext";
import {UserDashboardData, UserProjectDashboard} from "../../interface/UserDashboard.interface";
import {UserNextContributionsView} from "./UserNextContributionsView";
import {UserPastContributionsView} from "./UserPastContributionsView";
import {ComingSoonFeatures} from "./ComingSoonFeatures";
import UserMessagingView from "./messaging/UserMessagingView";
import styles from '../../styles/scoped/user/UserHome.module.css';
import {FolderSpecial, TrendingUp, AttachMoney, People, Assessment, EventNote, ExitToApp, Mail, ArrowBack} from "@mui/icons-material";
import {useNavigate, useLocation} from "react-router-dom";
import useAxios from "../../hooks/useAxios";
import {UserProjectContext} from "../../contexts/UserProjectContext";
import {Project} from "../../Models/Project";
import {USER_DASHBOARD_URL, USER_PROJECT_DASHBOARD_URL, PROJECT_STATUS} from "../../utils/constant";
import { logger } from '../../utils/logger';
import { useLogout } from '../../hooks/useLogout';
import { useUnreadCount } from '../../api/messages/useUnreadCount';


// Composant interne qui utilise le contexte des messages
const UserHomeContent = () => {
    const [dashboardData, setDashboardData] = useState<UserDashboardData | null>(null);
    const [selectedProjectDashboardData, seSelectedProjectDashboardData] = useState<UserProjectDashboard | null>(null);
    const [selectedProjectUuid, setSelectedProjectUuid] = useState<string>("");
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<Project | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const {getWithAxios} = useAxios();
    const logout = useLogout();
    const navigate = useNavigate();
    const location = useLocation();
    const { data: unreadCount = 0 } = useUnreadCount();

    // Vérifier si on est sur la page messages
    const isMessagingView = location.pathname === '/user/messages';

    // Charger les données du dashboard
    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await getWithAxios(USER_DASHBOARD_URL);

            // Filtrer les projets pour n'afficher que STARTED et ARCHIVED (exclure DRAFT)
            const visibleProjects = response.projects.filter(
                (p: any) => p.status === PROJECT_STATUS.STARTED || p.status === PROJECT_STATUS.ARCHIVED
            );

            const mappedData: UserDashboardData = {
                user: response.user,
                projects: visibleProjects
            };

            setDashboardData(mappedData);

            // Convertir les projets pour le contexte
            const projectObjects = visibleProjects.map((p: any) => new Project(p));
            setProjects(projectObjects);

            if (mappedData.projects.length > 0) {
                const firstProject = mappedData.projects[0];
                setSelectedProjectUuid(firstProject.uuid);
                setSelectedProject(projectObjects[0]);

                if (firstProject.uuid) {
                    await loadProjectDashboard(firstProject.uuid);
                }
            }
        } catch (err: any) {
            logger.error("Erreur lors du chargement du dashboard:", err);
            setError(err.message || 'Erreur inconnue');
        } finally {
            setLoading(false);
        }
    };

    const loadProjectDashboard = async (projectUuid: string) => {
        try {
            const response = await getWithAxios(USER_PROJECT_DASHBOARD_URL(projectUuid));

            // Le backend retourne directement les données au bon format
            if (response.success) {
                seSelectedProjectDashboardData(response as UserProjectDashboard);
            } else {
                throw new Error(response.message || 'Erreur lors du chargement du projet');
            }
        } catch (err: any) {
            logger.error("Erreur lors du chargement des données du projet:", err);
            setError(err.message);
        }
    };

    const handleProjectSelection = async (event: SelectChangeEvent<unknown>) => {
        const projectUuid = event.target.value as string;
        setSelectedProjectUuid(projectUuid);
        await loadProjectDashboard(projectUuid);
    };


    const userShares = selectedProjectDashboardData?.metrics.userShares || 0;
    const totalShares = selectedProjectDashboardData?.metrics.totalShares || 0;

    const projectMetrics = selectedProjectDashboardData ?   [
        {
            label: "Tu contribues",
            value: selectedProjectDashboardData.metrics.userContribution.formattedAmount,
            subtitle: "chaque mois",
            icon: <AttachMoney/>,
            shares: totalShares > 0 ? `${userShares} part${userShares > 1 ? 's' : ''} sur ${totalShares}` : null
        },
        {
            label: "Vous êtes",
            value:selectedProjectDashboardData.metrics.totalParticipants.toString(),
            subtitle: "membres",
            icon: <People/>
        },
        {
            label: "Objectif commun",
            value:selectedProjectDashboardData.metrics.targetAmount.formattedAmount,
            subtitle: "à atteindre ensemble",
            icon: <Assessment/>
        },
        {
            label: "Tu reçois le",
            value: (() => {
                const {collectionDates} =selectedProjectDashboardData.metrics;
                switch (collectionDates.status) {
                    case 'UPCOMING':
                        return collectionDates.next?.remainingCount === 1
                            ? collectionDates.next.formattedDate
                            : `${collectionDates.next?.formattedDate} (+${(collectionDates.next?.remainingCount || 1) - 1} autres)`;
                    case 'COMPLETED':
                        return "Tout est fait !";
                    case 'NONE':
                    default:
                        return "Rien de prévu";
                }
            })(),
            subtitle:selectedProjectDashboardData.metrics.collectionDates.summary.formattedSummary,
            icon: <EventNote/>
        }
    ] : [];

    if (loading) {
        return (
            <div className={styles.userContainer}>
                <div className={`${styles.contentWrapper} ${styles.loadingContainer}`}>
                    <div className={styles.loadingSpinner}></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.userContainer}>
                <div className={styles.contentWrapper}>
                    <div className={styles.errorCard}>
                        <p>Erreur: {error}</p>
                        <button onClick={loadDashboardData}>Réessayer</button>
                    </div>
                </div>
            </div>
        );
    }

    if (!dashboardData) {
        return (
            <div className={styles.userContainer}>
                <div className={styles.contentWrapper}>
                    <div className={styles.loadingCard}>
                        <p>Aucune donnée disponible</p>
                    </div>
                </div>
            </div>
        );
    }

    const userProjectContextValue = {
        projects,
        updateProjects: setProjects,
        selectedProject,
        updateSelectedProject: setSelectedProject,
    };

    return (
        <UserProjectContext.Provider value={userProjectContextValue}>
            <div className={styles.userContainer}>
                <div className={styles.contentWrapper}>
                    {/* Barre d'actions */}
                <div style={{
                    display: 'flex',
                    gap: '0.75rem',
                    marginBottom: '1rem',
                    alignItems: 'center'
                }}>
                    {/* Bouton Messages */}
                    <button
                        style={{
                            padding: '0.5rem 1.25rem',
                            borderRadius: '8px',
                            border: isMessagingView ? '2px solid var(--primary-color, #667eea)' : 'none',
                            background: isMessagingView ? 'rgba(102, 126, 234, 0.1)' : 'var(--primary-color, #667eea)',
                            color: isMessagingView ? 'var(--primary-color, #667eea)' : '#fff',
                            fontWeight: 600,
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(102,126,234,0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}
                        onClick={() => navigate(isMessagingView ? '/user/dashboard' : '/user/messages')}
                    >
                        {isMessagingView ? (
                            <ArrowBack fontSize="small" />
                        ) : (
                            <Badge badgeContent={unreadCount} color="error" max={99}>
                                <Mail fontSize="small" />
                            </Badge>
                        )}
                        {isMessagingView ? 'Retour' : 'Messages'}
                    </button>

                    {/* Bouton de déconnexion */}
                    <button
                        style={{
                            padding: '0.5rem 1.25rem',
                            borderRadius: '8px',
                            border: 'none',
                            background: 'rgba(0,0,0,0.05)',
                            color: 'var(--text-secondary, #666)',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}
                        onClick={logout}
                    >
                        <ExitToApp fontSize="small" />
                        Déconnexion
                    </button>
                </div>

                {/* Affichage conditionnel : Messagerie ou Dashboard */}
                {isMessagingView ? (
                    <UserMessagingView />
                ) : (
                    <>
                        {/* En-tête avec accueil utilisateur */}
                        <div className={styles.header}>
                            <div className={styles.userWelcome}>
                                <Avatar className={styles.userAvatar}>
                                    {dashboardData.user.firstname?.charAt(0) || 'U'}
                                </Avatar>
                                <div>
                                    <h1 className={styles.welcomeTitle}>
                                        Salut {dashboardData.user.firstname} !
                                    </h1>
                                    <p className={styles.welcomeSubtitle}>
                                        Voici un aperçu de tes projets
                                    </p>
                                </div>
                            </div>

                            {/* Sélecteur de projet */}
                            {dashboardData.projects.length > 0 && (
                                <div className={styles.projectSelector}>
                                    <label className={styles.selectorLabel}>
                                        <FolderSpecial sx={{mr: 0.5}}/>
                                        Tu participes à :
                                    </label>
                                    <Select
                                        className={styles.styledSelect}
                                        value={selectedProjectUuid}
                                        onChange={handleProjectSelection}
                                        displayEmpty
                                    >
                                        {dashboardData.projects?.map((project) => (
                                            <MenuItem key={project.uuid} value={project.uuid}>
                                                <FolderSpecial sx={{mr: 1, color: 'var(--primary-color)'}}/>
                                                {project.name}
                                                <Chip
                                                    className={styles.projectBadge}
                                                    label={project.status}
                                                    size="small"
                                                />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </div>
                            )}
                        </div>

                        {/* Section Coming Soon - Paiements et Retraits */}
                        <ComingSoonFeatures />

                        {dashboardData.projects.length === 0 ? (
                    <Card className={styles.contributionsSection} style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                        <CardContent>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '1.5rem'
                            }}>
                                <FolderSpecial
                                    sx={{
                                        fontSize: '5rem',
                                        color: 'var(--primary-color)',
                                        opacity: 0.3
                                    }}
                                />
                                <div>
                                    <h2 style={{
                                        fontSize: '1.8rem',
                                        fontWeight: 700,
                                        color: 'var(--text-primary)',
                                        marginBottom: '1rem'
                                    }}>
                                        Pas encore de projet
                                    </h2>
                                    <p style={{
                                        fontSize: '1.1rem',
                                        color: 'var(--text-secondary)',
                                        marginBottom: '0.5rem',
                                        lineHeight: 1.6
                                    }}>
                                        Tu n'as pas encore rejoint de projet.
                                    </p>
                                    <p style={{
                                        fontSize: '1rem',
                                        color: 'var(--text-secondary)',
                                        opacity: 0.8,
                                        lineHeight: 1.6
                                    }}>
                                        Contacte un admin pour rejoindre l'aventure !
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ) : selectedProjectDashboardData ? (
                    <>
                        {/* Métriques du projet */}
                        <div className={styles.metricsGrid}>
                            {projectMetrics.map((metric, index) => (
                                <div key={index} className={styles.metricCard}>
                                    <div className={styles.metricHeader}>
                                        <div className={styles.metricIconContainer}>
                                            {metric.icon}
                                        </div>
                                        <h4 className={styles.metricLabel}>{metric.label}</h4>
                                    </div>
                                    <h2 className={styles.metricValue}>{metric.value}</h2>
                                    <p className={styles.metricSubtitle}>{metric.subtitle}</p>
                                    {metric.shares && (
                                        <p className={styles.metricShares}>{metric.shares}</p>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Grille du tableau de bord */}
                        <div className={styles.dashboardGrid}>
                            {/* Bloc contributions fusionné */}
                            <Card className={styles.contributionsSection}>
                                <div className={styles.sectionHeader}>
                                    <div className={styles.sectionIcon}>
                                        <TrendingUp/>
                                    </div>
                                    <h3 className={styles.sectionTitle}>Tes contributions</h3>
                                </div>
                                <CardContent className={styles.sectionContent}>
                                    <div className={styles.contributionsGrid}>
                                        <div className={styles.contributionBlock}>
                                            <h4 className={styles.contributionTitle}>À venir</h4>
                                            <div className={styles.contributionContent}>
                                                <div className={styles.contributionStats}>
                                                    <span className={styles.contributionValue}>
                                                        {selectedProjectDashboardData.contributions.statistics.totalUpcoming}
                                                    </span>
                                                    <span className={styles.contributionBadge}>Prochainement</span>
                                                </div>
                                                <p className={styles.contributionDescription}>
                                                    Ce que tu dois encore verser
                                                </p>
                                                <div className={styles.contributionProgress}>
                                                    <div
                                                        className={styles.contributionProgressBar}
                                                        style={{
                                                            width: `${selectedProjectDashboardData.contributions.statistics.completionRate}%`
                                                        }}
                                                    ></div>
                                                </div>
                                                <UserNextContributionsView
                                                    contributions={selectedProjectDashboardData.contributions.upcoming}
                                                />
                                            </div>
                                        </div>
                                        <div className={styles.contributionBlock}>
                                            <h4 className={styles.contributionTitle}>Déjà versées</h4>
                                            <div className={styles.contributionContent}>
                                                <div className={styles.contributionStats}>
                                                    <span className={styles.contributionValue}>
                                                        {selectedProjectDashboardData.contributions.statistics.totalCompleted}
                                                    </span>
                                                    <span className={styles.contributionBadge}>Merci !</span>
                                                </div>
                                                <p className={styles.contributionDescription}>
                                                    Bravo pour ta participation !
                                                </p>
                                                <div className={styles.contributionProgress}>
                                                    <div
                                                        className={styles.contributionProgressBar}
                                                        style={{width: '100%'}}
                                                    ></div>
                                                </div>
                                                <UserPastContributionsView
                                                    contributions={selectedProjectDashboardData.contributions.past}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </>
                ) : (
                    <div className={styles.loadingCard}>
                        <div className={styles.loadingSpinner}></div>
                        <p>On prépare tout ça...</p>
                    </div>
                )}
                    </>
                )}
                </div>
            </div>
        </UserProjectContext.Provider>
    );
};

// Export principal
export const UserHome = () => {
    return <UserHomeContent />;
};
